import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const Register = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  // Get initial data from location state (from login page or OTP verification)
  useEffect(() => {
    console.log('🔍 Register Page State Check:', {
      locationState: location.state,
      isMerchantFromState: location.state?.isMerchant,
      roleParam: searchParams.get('role'),
      sessionStorage: sessionStorage.getItem('isMerchant'),
      localStorage: localStorage.getItem('merchantIntent')
    });

    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }
    if (location.state?.phone) {
      // Phone is already verified via OTP
      setFormData(prev => ({ ...prev, phone: location.state.phone }));
    }
    const roleParam = searchParams.get('role');
    const isMerchant = location.state?.isMerchant || roleParam === 'merchant' || sessionStorage.getItem('isMerchant') === 'true';

    console.log('🔍 Is Merchant:', isMerchant);

    // Store merchant intent for navbar detection
    if (isMerchant) {
      localStorage.setItem('merchantIntent', 'true');
      sessionStorage.setItem('isMerchant', 'true');
      setFormData(prev => {
        const newData = { ...prev, role: 'merchant' };
        console.log('✅ Setting role to merchant:', newData);
        return newData;
      });
    }
  }, [location.state, searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // For merchants, validate phone number
    if (formData.role === 'merchant' && !formData.phone) {
      setError('Phone number is required for merchant accounts');
      return;
    }

    // For regular users, validate date of birth and gender
    if (formData.role !== 'merchant') {
      if (!formData.dateOfBirth) {
        setError('Date of birth is required');
        return;
      }
      if (!formData.gender) {
        setError('Gender is required');
        return;
      }
    }

    setLoading(true);

    // Combine first and last name
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    console.log('📝 Submitting registration:', {
      fullName,
      email: formData.email,
      role: formData.role,
      phone: formData.phone
    });

    const result = await register(
      fullName,
      formData.email,
      formData.password,
      formData.role,
      formData.phone,
      formData.dateOfBirth,
      formData.gender
    );
    
    if (result.success) {
      // If merchant, redirect to add business. Otherwise, go to dashboard
      if (formData.role === 'merchant') {
        navigate('/merchant/add-business');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-4 py-12">
        {/* Back Button */}
        <button
          onClick={() => {
            const target = location.state?.from || '/login';
            navigate(target, { state: { from: '/' } });
          }}
          className="mb-8 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {formData.role === 'merchant' ? 'Create your Merchant Account' : 'Create your LOCU account'}
        </h1>
        {formData.role === 'merchant' && (
          <p className="text-gray-600 mb-8">
            Sign up as a merchant to list your business on LOCU
          </p>
        )}

        {/* Debug Info - Remove after testing */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
            <strong>Debug:</strong> Current Role = {formData.role}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {formData.role === 'merchant' ? 'Business Email' : 'Your email'}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={formData.role === 'merchant' ? 'manager@restaurant.com' : 'you@example.com'}
                value={formData.email}
                onChange={handleChange}
              />
              {formData.role === 'merchant' && (
                <p className="mt-1 text-xs text-gray-500">
                  We'll use this for invoices, settlements, and important updates
                </p>
              )}
            </div>

            {/* Phone Number for Merchants */}
            {formData.role === 'merchant' && (
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone Number
                </label>
                <div className="flex">
                  <div className="flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50">
                    <span className="text-sm text-gray-700">🇮🇳 +91</span>
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength="10"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, phone: value });
                    }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  For quick communication and support
                </p>
              </div>
            )}

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>

            {/* Date of Birth - Only for regular users, not merchants */}
            {formData.role !== 'merchant' && (
              <>
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                    Date of birth
                  </label>
                  <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used for building your rent credit profile
                  </p>
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </>
            )}


            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Minimum 6 characters"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Terms and Conditions */}
          <p className="text-xs text-gray-600 leading-relaxed">
            By clicking "Continue" you agree to the{' '}
            <Link to="/terms" className="text-primary-600 hover:text-primary-700 underline">
              LOCU Rewards Terms & Conditions
            </Link>
            , the{' '}
            <Link to="/terms-of-use" className="text-primary-600 hover:text-primary-700 underline">
              LOCU Services Terms of Use
            </Link>
            , and provide your authorization for LOCU to obtain your credit reports for as long as you use the LOCU Rewards Credit Education Services to display to you for educational purposes and providing data insights. You may withdraw this authorization at any time by contacting{' '}
            <a href="mailto:support@locu.com" className="text-primary-600 hover:text-primary-700 underline">
              support@locu.com
            </a>
            .
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating account...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
